/**
 * RectanglePreview — small dimensioned 2D SVG of the manual rectangle.
 * `compact` renders a modest square thumbnail for line-cards; the
 * default renders a slightly larger dimensioned preview.
 * Provenance label is a concise "Manual" chip — not a paragraph.
 */
export default function RectanglePreview({ width_mm, length_mm, compact = false, line = false, tile = false }) {
  const w = Number(width_mm);
  const l = Number(length_mm);
  const valid = Number.isFinite(w) && Number.isFinite(l) && w > 0 && l > 0;

  if (!valid) {
    return (
      <div
        data-testid="rectangle-preview-empty"
        className={`${compact ? "w-16 h-16" : tile ? "h-full w-full" : line ? "w-full h-28" : "w-full h-32"} flex items-center justify-center border border-dashed border-slate-300 rounded-lg bg-slate-50 text-[10px] text-slate-500 text-center`}
      >
        {compact ? "—" : "Enter W & L to preview"}
      </div>
    );
  }

  const boxW = compact ? 100 : tile ? 150 : line ? 220 : 320;
  const boxH = compact ? 100 : tile ? 106 : line ? 130 : 160;
  const margin = compact ? 8 : tile ? 15 : line ? 25 : 28;
  const availW = boxW - margin * 2;
  const availH = boxH - margin * 2;
  const scale = Math.min(availW / w, availH / l);
  const drawW = w * scale;
  const drawH = l * scale;
  const x = (boxW - drawW) / 2;
  const y = (boxH - drawH) / 2;

  const wStr = w.toFixed(w % 1 === 0 ? 0 : 2);
  const lStr = l.toFixed(l % 1 === 0 ? 0 : 2);

  return (
    <svg
      data-testid="rectangle-preview-svg"
      viewBox={`0 0 ${boxW} ${boxH}`}
      className={`${compact ? "w-16 h-16" : tile ? "h-full w-full" : line ? "w-full h-28" : "w-full h-32"} rounded-lg ${tile ? "bg-white" : "border border-slate-200 bg-[linear-gradient(#f8fafc_1px,transparent_1px),linear-gradient(90deg,#f8fafc_1px,transparent_1px)] bg-[size:12px_12px]"}`}
      xmlns="http://www.w3.org/2000/svg"
      aria-label={`Rectangle ${wStr}mm by ${lStr}mm`}
    >
      <rect x={x} y={y} width={drawW} height={drawH} fill="#F1F5F9" stroke="#0F172A" strokeWidth={compact ? 1 : 1.4} />
      {!compact && !tile && (
        <>
          <g stroke="#475569" strokeWidth="1" fill="none">
            <line x1={x} y1={y + drawH + 8} x2={x + drawW} y2={y + drawH + 8} />
            <line x1={x} y1={y + drawH + 4} x2={x} y2={y + drawH + 12} />
            <line x1={x + drawW} y1={y + drawH + 4} x2={x + drawW} y2={y + drawH + 12} />
            <line x1={x + drawW + 8} y1={y} x2={x + drawW + 8} y2={y + drawH} />
            <line x1={x + drawW + 4} y1={y} x2={x + drawW + 12} y2={y} />
            <line x1={x + drawW + 4} y1={y + drawH} x2={x + drawW + 12} y2={y + drawH} />
          </g>
          <text x={x + drawW / 2} y={y + drawH + 22} textAnchor="middle" fontSize="11" fill="#0F172A">
            {wStr} mm
          </text>
          <text
            x={x + drawW + 20} y={y + drawH / 2}
            transform={`rotate(-90 ${x + drawW + 20} ${y + drawH / 2})`}
            textAnchor="middle" fontSize="11" fill="#0F172A"
          >
            {lStr} mm
          </text>
        </>
      )}
    </svg>
  );
}
